import { inject, injectable } from 'inversify-props';
import { IHttpClient, ILogger, ISettings } from '../common';
import { PagedResult, TruckMount } from '../../models';
import { IBaseService } from '../BaseService';

export interface ITruckMountService extends IBaseService<TruckMount, number> {
	getMultiple(ids: number[]): Promise<TruckMount[]>;
}

@injectable()
export class TruckMountService implements ITruckMountService {

	private cache: Promise<TruckMount[]>;

	constructor(
		@inject('IHttpClient') private httpClient: IHttpClient,
		@inject('ILogger') private logger: ILogger,
		@inject('ISettings') private settings: ISettings) { }

	public get(id: number): Promise<TruckMount> {
		const url = `${this.settings.baseUrl}${this.settings.truckMountUrl}/${id}`;
		return this.httpClient.get<TruckMount>(url);
	}

	public getMultiple(ids: number[]): Promise<TruckMount[]> {
		const url = `${this.settings.baseUrl}${this.settings.truckMountUrl}/multiple/${ids.join(',')}`;
		return this.httpClient.get<TruckMount[]>(url);
	}

	public page(pageIndex: number, pageSize: number): Promise<PagedResult<TruckMount>> {
		const url = `${this.settings.baseUrl}${this.settings.truckMountUrl}/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<TruckMount>>(url);
	}

	public list(): Promise<TruckMount[]> {
		const url = `${this.settings.baseUrl}${this.settings.truckMountUrl}`;
		if (!this.cache) { this.cache = this.httpClient.get<TruckMount[]>(url); }
		return this.cache;
	}

	public save(model: TruckMount): Promise<TruckMount> {
		this.cache = null; // INVALIDATE CACHE
		const payload = {
			id: model.id,
			name: model.name,
			width: model.width ? parseFloat(model.width.toString()) : null,
			length: model.length ? parseFloat(model.length.toString()) : null,
			description: model.description,
			sortBy: (model.sortBy != null) ? parseInt(model.sortBy.toString(), null) : null,
		};

		let url = `${this.settings.baseUrl}${this.settings.truckMountUrl}`;
		if (model.id !== null && model.id !== undefined) {
			url += `/${model.id}`;
			return this.httpClient.put(url, payload);
		}
		return this.httpClient.post(url, payload);
	}

	public delete(id: number): Promise<any> {
		this.cache = null; // INVALIDATE CACHE
		const url = `${this.settings.baseUrl}${this.settings.truckMountUrl}/${id}`;
		return this.httpClient.delete(url);
	}

}
